import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectBuchungen,
  selectDauerbuchungen,
  selectKategorien,
} from 'modules/finanzen/features/finanzenSlice';
import { istEinnahme, betragVon, buchungenDerSerie } from 'modules/finanzen/features/berechnung';
import { kategorieIcon, zahlwegIcon } from 'modules/finanzen/features/konstanten';
import { formatKurs } from 'core/utils/format';
import BackToOverview from 'core/components/BackToOverview';
import GesichertGate from 'modules/finanzen/components/GesichertGate';
import ModulTabs from 'modules/finanzen/components/ModulTabs';
import SerieBearbeitenModal from 'modules/finanzen/components/SerieBearbeitenModal';
import DauerauftragVorschlaege from 'modules/finanzen/components/DauerauftragVorschlaege';
import DauerauftragArchiv from 'modules/finanzen/components/DauerauftragArchiv';

/**
 * Übersicht der Daueraufträge: automatisch erkannte, wiederkehrende Zahlungen.
 * Daueraufträge erzeugen KEINE Buchungen mehr – Buchungen kommen ausschließlich
 * über Importe/Erfassen und werden mit ihrer Serie verknüpft. Nur im gesicherten
 * Modus erreichbar.
 */
export default function DauerauftraegePage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>
      <GesichertGate>
        <Verwaltung />
      </GesichertGate>
    </div>
  );
}

/** Entsperrte Ansicht: Vorschläge + gespeicherte Serien. */
function Verwaltung() {
  const dauerbuchungen = useSelector(selectDauerbuchungen);
  const buchungen = useSelector(selectBuchungen);
  const eigeneKategorien = useSelector(selectKategorien);
  const [bearbeitung, setBearbeitung] = useState(null);

  const aktive = dauerbuchungen.filter((d) => !d.abgelehnt);
  const archiviert = dauerbuchungen.filter((d) => d.abgelehnt);

  return (
    <>
      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">🔁 Daueraufträge</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Automatisch aus deinen Buchungen erkannt. Bestätigte Serien lassen sich gemeinsam bearbeiten –
          neue Import-Buchungen werden ihnen automatisch zugeordnet.
        </p>
      </header>

      <ModulTabs />

      <DauerauftragVorschlaege />

      {aktive.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch keine gespeicherten Daueraufträge. Sobald eine Zahlung in mehreren Monaten in Folge
          auftaucht, erscheint sie oben als Vorschlag – „Als Dauerauftrag“ speichert die Serie.
        </div>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-paper">
          {aktive.map((d) => (
            <DauerZeile
              key={d.uuid}
              dauerbuchung={d}
              anzahl={buchungenDerSerie(buchungen, d.uuid).length}
              eigeneKategorien={eigeneKategorien}
              onClick={() => setBearbeitung(d)}
            />
          ))}
        </ul>
      )}

      <DauerauftragArchiv archiviert={archiviert} />

      {bearbeitung && <SerieBearbeitenModal dauerauftrag={bearbeitung} onClose={() => setBearbeitung(null)} />}
    </>
  );
}

/** Eine Serien-Zeile; klickbar zum gemeinsamen Bearbeiten. */
function DauerZeile({ dauerbuchung, anzahl, eigeneKategorien, onClick }) {
  const einnahme = istEinnahme(dauerbuchung);
  const vorzeichen = einnahme ? '+' : '−';
  const ton = einnahme ? 'text-gain' : 'text-loss';
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-ink/5"
      >
        <span className="text-xl" aria-hidden="true">
          {kategorieIcon(dauerbuchung.typ, dauerbuchung.kategorie, eigeneKategorien)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-ink">
            {dauerbuchung.notiz || dauerbuchung.kategorie}
          </span>
          <span className="block text-xs text-ink-muted">
            {dauerbuchung.kategorie} · {anzahl} Buchung(en) · {zahlwegIcon(dauerbuchung.zahlweg)}{' '}
            {dauerbuchung.zahlweg}
          </span>
        </span>
        <span className={`shrink-0 font-semibold tabular-nums ${ton}`}>
          {vorzeichen} Ø {formatKurs(betragVon(dauerbuchung))}
        </span>
      </button>
    </li>
  );
}
